<template>
  <div
    :class="[
      'message-container',
      {
        'no-bottom-padding': showTimestamp,
        'no-padding': !showTimestamp,
      },
    ]"
  >
    <ChatTimestamp v-if="showTimestamp" class="timestamp" :date="date" />
    <div
      v-if="!message.deletedAt"
      :class="[
        {
          'is-reply': replyMessage,
        },
      ]"
    >
      <div v-if="replyMessage" class="reply-container">
        <FriedIcon icon="email-action-reply" :size="IconSize.Medium" />
        <div class="reply-content">
          <FriedH5> {{ replyHeader }} </FriedH5>
          <FriedParagraph :size="ParagraphSize.Small">{{ replyMessage.text }}</FriedParagraph>
        </div>
      </div>
      <ChatMessageContent
        :user="user"
        :participants="participants"
        :message="message"
        :messages="messages"
        :owner="owner"
        :locale="locale"
        :can-edit="canEdit"
        :previous-room-owners="previousRoomOwners"
        @delete="$emit('delete', $event)"
        @reply="$emit('reply', $event)"
      />
      <MessageReadBy v-if="lastReadsExcludingSender.length" :reads="lastReadsExcludingSender" />
    </div>
  </div>
</template>

<script lang="ts">
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import type { PropType } from 'vue';
import { watch, computed, defineComponent, toRefs, ref } from 'vue';
import { ParagraphSize, AvatarSize, IconSize } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { AvatarHelper } from '@getaccept/lib-shared-new/src/helpers/avatars.helper';
import type { ChatParticipant, LastReadBy, Message, ReadBy } from '../types';
import { ChatHelper } from '../helpers/chat.helper';
import type { Participant } from '../../types';
import ChatTimestamp from './ChatTimestamp.vue';
import MessageReadBy from './MessageReadBy.vue';
import ChatMessageContent from './ChatMessageContent.vue';

export default defineComponent({
  components: {
    ChatTimestamp,
    MessageReadBy,
    ChatMessageContent,
  },
  props: {
    user: {
      type: Object as PropType<User | Participant>,
      default: undefined,
    },
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    message: {
      type: Object as PropType<Message>,
      default: undefined,
    },
    messages: {
      type: Array as PropType<Array<Message>>,
      default: () => [],
    },
    owner: {
      type: Object as PropType<ChatParticipant>,
      default: undefined as ChatParticipant,
    },
    lastReads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
    locale: {
      type: String,
      default: '',
    },
    tabVisible: {
      type: Boolean,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    previousRoomOwners: {
      type: Array as PropType<Array<User>>,
      default: () => [],
    },
  },
  emits: ['read', 'delete', 'reply'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { participants, user, message, messages, lastReads, owner } = toRefs(props);

    const {
      name,
      thumbUrl,
      date,
      fullTimestamp,
      isBySender,
      replyMessage,
    } = ChatHelper.getMessageState(
      message.value,
      user.value as Participant,
      participants.value as Participant[],
      messages.value,
      owner.value,
      true,
      props.locale
    );

    const previousMessage = computed(() =>
      ChatHelper.getPreviousMessage(message.value, messages.value)
    );

    const showTimestamp = computed(() =>
      ChatHelper.showTimestamp(messages.value, previousMessage.value, message.value.createdAt)
    );

    const messageLastReads = computed(() =>
      lastReads.value.filter(({ messageId }) => messageId === message.value.id)
    );

    const lastReadsExcludingSender = computed(() =>
      messageLastReads.value.filter(
        ({ readBy }) => !ChatHelper.isSameParticipant(message.value.owner, readBy.participant)
      )
    );

    const sender = computed(() => {
      if (message.value.owner?.participantId) {
        return participants.value.find(({ id }) => id === message.value.owner.participantId);
      }
      return user.value;
    });

    const messageRead = ref(false);

    watch(
      () => props.tabVisible,
      () => {
        if (props.tabVisible && !messageRead.value) {
          const hasRead = message.value.reads.find(({ participant }: ReadBy) =>
            ChatHelper.isSameParticipant(owner.value, participant)
          );
          if (!hasRead && !message.value.isDraft) {
            emit('read', message.value.id);
            messageRead.value = true;
          }
        }
      },
      { immediate: true }
    );

    const replyHeader = computed(() => {
      const { participantId } = replyMessage?.owner || {};
      const isSameSender = ChatHelper.isSameParticipant(replyMessage?.owner, owner.value);
      const participant =
        participantId && participants.value.find(({ id }) => id === participantId);
      const name = isSameSender ? t('you').toLowerCase() : getReplyName(participant);
      const prefix = isBySender ? `${t('you')} ` : '';
      const repliedTo = prefix ? t('dr_chat_replied_to').toLowerCase() : t('dr_chat_replied_to');
      return `${prefix}${repliedTo} ${name}`;
    });

    const getReplyName = (participant: Participant) =>
      participant ? AvatarHelper.getDisplayName(participant) : user.value.fullName;

    return {
      name,
      thumbUrl,
      date,
      fullTimestamp,
      isBySender,
      showTimestamp,
      replyMessage,
      messageLastReads,
      lastReadsExcludingSender,
      IconSize,
      sender,
      AvatarSize,
      ParagraphSize,
      t,
      replyHeader,

      previousMessage,
      messageRead,
      getReplyName,
    };
  },
});
</script>

<style lang="scss" scoped>
.message-container {
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  padding: var(--spacing-50) 0;

  .reply-container {
    svg {
      margin: auto;
    }

    padding: var(--spacing-100) var(--spacing-150) var(--spacing-100) var(--spacing-100);
    display: grid;
    grid-template-columns: 2rem 1fr auto;
    column-gap: var(--spacing-75);

    .reply-content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-50);

      h5,
      p {
        color: var(--text-gray);
      }
    }
  }

  &.no-bottom-padding {
    padding-bottom: 0;
  }

  &.no-padding {
    padding: 0;
  }

  .is-reply {
    background-color: var(--gray-98);
  }

  .timestamp {
    margin: var(--spacing-100) 0 var(--spacing-150);
  }
}
</style>
